import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, effect, inject, input, linkedSignal, output, signal, viewChild } from '@angular/core';
import { EntityView, EntityViewRecordScope } from '@jms/model';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { SettingsPanelComponent, createSettingsPanelWizard } from '@workbuddy/ui/v2/blocks/settings.panel';
import { SideDialogComponent } from '@workbuddy/ui/v2/blocks/side.dialog';
import { resolveDefaultRecordScope, resolveRecordScopeOptions } from '../../state/features/with.metadata';
import { viewSettingsBridgeStyles, viewSettingsStyleId } from './view.settings.theme';
import { ViewSettingsConfig, ViewSettingsValue } from './view.settings.model';
import { ViewAdapterContext, ViewAdapterDeps } from './adapters/view.step.adapter';
import { createSetupAdapter } from './adapters/setup.step.adapter';
import { createConfigureAdapter } from './adapters/configure.step.adapter';

export { ViewSettingsValue };

const SETUP_STEP_ID = 'setup';
const CONFIGURE_STEP_ID = 'configure';

function modelFromConfig(config: ViewSettingsConfig | null | undefined): Partial<EntityView> {

  const view = config?.view;
  const restrictedScope = config?.recordScopes?.length === 1 ? resolveDefaultRecordScope(config?.metadata, config.recordScopes) : undefined;
  if (!view) {
    // No view yet — seed with the default scope so the setup step's
    // tab picker has something to highlight when first opened.
    const scope = restrictedScope ?? resolveDefaultRecordScope(config?.metadata, config?.recordScopes);
    return scope ? { recordScope: scope } : {};
  }
  const isEdit = (config?.mode ?? (view._id ? 'edit' : 'create')) === 'edit';
  // In create mode the seeded view (typically the current preview) carries
  // an `_id` — strip it so `initialStep` can use `_id` as the edit-vs-create
  // discriminator without consulting external state.
  if (!isEdit) {
    const { _id, ...rest } = view;
    return restrictedScope ? { ...rest, recordScope: restrictedScope } : rest;
  }
  return restrictedScope ? { ...view, recordScope: restrictedScope } : { ...view };
}

function hasSecondaryScope(config: ViewSettingsConfig | null | undefined): boolean {

  return resolveRecordScopeOptions(config?.metadata, config?.recordScopes).length > 1;
}

@Component({
  selector: 'view-settings',
  templateUrl: './view.settings.component.html',
  styleUrls: ['./view.settings.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SettingsPanelComponent, SideDialogComponent],
  host: { class: 'wb-board-settings-panel' },
})
export class ViewSettingsComponent {

  private readonly document = inject(DOCUMENT);
  private readonly sideDialog = viewChild(SideDialogComponent);

  public $config = input.required<ViewSettingsConfig>({ alias: 'config' });
  public $isOpen = input<boolean>(false, { alias: 'isOpen' });
  public $mode = input<'overlay' | 'push'>('overlay', { alias: 'mode' });

  public previewChange = output<ViewSettingsValue>();
  public previewViewChange = output<ViewSettingsValue>();
  public previewRequestChange = output<ViewSettingsValue>();
  public valueChange = output<ViewSettingsValue>();
  public saved = output<void>();
  public saveAsNew = output<void>();
  public cancelled = output<void>();
  public reset = output<void>();
  public deleted = output<void>();
  public closed = output<void>();

  private readonly $model = computed(() => modelFromConfig(this.$config()));
  // Tracks the wizard's live model's recordScope so it reflects step 1's
  // commit (not just what's derived from $config). Owned by the component
  // because adapter factories run before `wizard` is initialized — we
  // can't reference `this.wizard` in field initializers. An effect below
  // mirrors the wizard's model into this signal.
  private readonly $recordScope = signal<EntityViewRecordScope | undefined>(undefined);

  // Intra-step kanban column/row selection for option dedup. linkedSignal
  // resets to the source on $model emissions; the form's onUpdate writes
  // imperatively in between (routed through ctx.onSelectionChange).
  private readonly $selectedColumn = linkedSignal(() => this.$model().kanban?.column ?? 'none');
  private readonly $selectedRow = linkedSignal(() => this.$model().kanban?.row ?? 'none');

  private readonly adapterDeps: ViewAdapterDeps = {
    $config: this.$config,
    $recordScope: this.$recordScope,
    $selectedColumn: this.$selectedColumn,
    $selectedRow: this.$selectedRow,
  };

  private readonly configureAdapter = createConfigureAdapter(this.adapterDeps);

  private readonly wizard = createSettingsPanelWizard<Partial<EntityView>, ViewAdapterContext>({
    model: this.$model,
    // Rebuild configure form once metadata/uiFields arrive — view-settings mounts with the
    // board and often builds the panel before the header query resolves. Without this, create
    // flows whose model stays `{}` keep the first empty field-option form forever.
    rebuildKey: computed(() => {
      const config = this.$config();
      return `${config?.metadata?.type ?? ''}:${config?.uiFields?.length ?? 0}:${config?.recordScopes?.join(',') ?? ''}`;
    }),
    // Skip the scope-gate step when the entity has no secondary filter,
    // or when editing a view that already carries a recordScope.
    initialStep: model => {

      if (!hasSecondaryScope(this.$config())) return CONFIGURE_STEP_ID;
      if (model._id || model.recordScope) return CONFIGURE_STEP_ID;
      return SETUP_STEP_ID;
    },
    context: () => ({
      onPreview: value => this.previewChange.emit(value),
      onPreviewView: value => { this.previewViewChange.emit(value); this.valueChange.emit(value); },
      onPreviewRequest: value => this.previewRequestChange.emit(value),
      onSelectionChange: (path, value) => {

        if (path === 'kanban.column') this.$selectedColumn.set(String(value));
        if (path === 'kanban.row') this.$selectedRow.set(String(value));
      },
      onReset: () => this.reset.emit(),
      onDelete: () => this.deleted.emit(),
      onCancel: () => this.cancelled.emit(),
      onSaveAsNew: () => this.onSaveAsNew(),
    }),
    steps: [
      {
        id: SETUP_STEP_ID,
        skipHistory: true,
        build: createSetupAdapter(this.adapterDeps).build,
        commit: values => ({
          name: values['name'] as string,
          recordScope: values['recordScope'] as EntityViewRecordScope,
        }),
        nextStep: () => CONFIGURE_STEP_ID,
      },
      {
        id: CONFIGURE_STEP_ID,
        terminal: true,
        build: this.configureAdapter.build,
        commit: (values: Record<string, unknown>) => this.configureAdapter.buildValue!(values, this.wizard.model()),
      },
    ],
    onDone: () => {

      // Wizard has already merged the terminal step's values into the
      // model — emit preview/value with the final shape, then fire
      // `saved` so the host can persist.
      const value: ViewSettingsValue = {
        view: this.wizard.model(),
        pageSize: this.wizard.model().pageSize ?? this.$config()?.pageSize ?? 50,
      };
      this.previewChange.emit(value);
      this.previewViewChange.emit(value);
      this.previewRequestChange.emit(value);
      this.valueChange.emit(value);
      this.saved.emit();
    },
  });

  protected readonly panel = computed(() => this.wizard.panel());

  public constructor() {

    DesignSystem.Mount(this.document, viewSettingsStyleId, viewSettingsBridgeStyles);

    // Mirror the wizard's live model scope into $recordScope so adapter
    // factory effects (filter-provider reconfiguration) react to step 1's
    // commits. Cannot live as a `computed` next to the field initializers
    // because `this.wizard` doesn't exist when those run.
    effect(() => this.$recordScope.set(this.wizard.model().recordScope));
  }

  private onSaveAsNew(): void {

    // "Save as new" is a non-wizard exit — emit the current form's
    // value but don't run the wizard's commit/onDone. The host treats
    // it as a separate persistence path (clones to a new view).
    const form = this.wizard.panel();
    if (!form) return;
    const value: ViewSettingsValue = {
      view: this.configureAdapter.buildValue!(form.value() as Record<string, unknown>, this.wizard.model()),
      pageSize: this.$config()?.pageSize ?? 50,
    };
    this.previewChange.emit(value);
    this.previewViewChange.emit(value);
    this.previewRequestChange.emit(value);
    this.valueChange.emit(value);
    this.saveAsNew.emit();
  }

  /** Start leave animation (do not clear isOpen yet). */
  protected requestClose(): void {

    this.sideDialog()?.close();
  }

  /** Leave finished — notify board to set isOpen=false. */
  protected onSideDialogClosed(): void {

    this.closed.emit();
  }
}
